// Row-level mutations for the grid views.
//
// Wraps the optimistic-update + revert pattern: apply the change to the
// local rows array immediately so the cell renders the new value with
// no flash, kick off the server call, and roll back if it fails. The
// row callback receives any error so the caller can toast.
//
// Currently exposes only the star toggle — name editing was lifted out
// when click-on-name was changed to "open the record" rather than
// "edit it inline". Edits happen via the slide-over panel's form.

import { useCallback } from 'react';
import type { Contact } from '@personical/cc-shared-types';
import type { ApiClient } from '../api.js';

interface RowMutations {
  onToggleStar(rowId: string, value: boolean): Promise<void>;
}

interface UseRowMutationsArgs {
  api: ApiClient;
  rows: Contact[] | null;
  setRows(updater: (prev: Contact[] | null) => Contact[] | null): void;
  onError?(err: Error): void;
}

export function useRowMutations({
  api,
  rows,
  setRows,
  onError
}: UseRowMutationsArgs): RowMutations {
  const onToggleStar = useCallback(
    async (rowId: string, value: boolean) => {
      const original = rows?.find((c) => c.id === rowId);
      if (!original) return;
      try {
        const saved = await api.setFavorite(rowId, value);
        setRows((prev) => prev?.map((c) => (c.id === rowId ? saved : c)) ?? prev);
      } catch (err) {
        onError?.(err as Error);
      }
    },
    [api, rows, setRows, onError]
  );

  return { onToggleStar };
}
